<template>
  <div class="event-config-wrap">
    <!-- 顶部事件名称标题 -->
    <div class="event-title">
      <i class="el-icon-arrow-left" @click="handleSaveAndBack"></i>
      <span>{{ eventTitle }}</span>
    </div>

    <!-- 事件基本信息 -->
    <div class="section-container">
      <div class="section-title">事件基本信息</div>
      <el-form :model="formData" ref="formRef" label-width="80px" class="event-form">
        <el-form-item label="事件名称" prop="eventName" :rules="eventNameRules">
          <el-input
            v-model="formData.eventName"
            placeholder="event_001"
            style="width: 260px"
            maxlength="40"
            show-word-limit
            :disabled="isViewMode || mode === 'edit'"
          ></el-input>
        </el-form-item>
        <el-form-item label="选择产品" prop="products" :rules="[{ required: true, message: '请选择产品' }]">
          <product-select
            multiple
            :value.sync="formData.products"
            placeholder="请选择产品"
            :isDefault="false"
            :disabled="isViewMode"
            popper-class="BI-select BI-select-product"
          ></product-select>
        </el-form-item>
        <el-form-item label="备注(选填)" prop="remark">
          <el-input
            v-model="formData.remark"
            type="textarea"
            placeholder=""
            :rows="3"
            style="width: 800px"
            maxlength="200"
            show-word-limit
            :disabled="isViewMode"
          ></el-input>
        </el-form-item>
      </el-form>
    </div>

    <!-- 事件定义 -->
    <div class="section-container">
      <div class="section-title">事件定义</div>

      <!-- 用户属性 -->
      <div class="condition-block">
        <div class="condition-header">
          <span class="condition-label">用户属性：</span>
        </div>
        <div class="condition-content">
          <div class="condition-list-wrapper" v-if="userAttributes.length > 0">
            <div class="condition-badge-center">且</div>
            <div class="condition-list">
              <div v-for="(item, index) in userAttributes" :key="'user-' + index" class="condition-item">
                <div class="condition-row">
                  <div class="condition-selects">
                    <el-select
                      v-model="item.typeValue"
                      placeholder="请选择"
                      class="condition-select"
                      @change="onUserAttrTypeChange(item, index)"
                      :disabled="isViewMode"
                    >
                      <el-option
                        v-for="type in getAvailableUserAttrTypes(index)"
                        :key="type.value"
                        :label="type.label"
                        :value="type.value"
                      ></el-option>
                    </el-select>

                    <el-select
                      v-if="getUserAttrConfig(item.typeValue)?.hasOperator"
                      v-model="item.operatorValue"
                      placeholder="请选择"
                      class="condition-select"
                      :disabled="isViewMode"
                    >
                      <el-option
                        v-for="op in getUserAttrConfig(item.typeValue)?.operators"
                        :key="op.value"
                        :label="op.label"
                        :value="op.value"
                      ></el-option>
                    </el-select>

                    <el-select
                      v-if="
                        getUserAttrConfig(item.typeValue)?.hasValue &&
                        getUserAttrConfig(item.typeValue)?.valueType === 'select'
                      "
                      v-model="item.value"
                      placeholder="请选择"
                      class="condition-select"
                      :disabled="isViewMode"
                      :multiple="['country_code', 'media_source'].includes(item.typeValue)"
                      collapse-tags
                    >
                      <el-option
                        v-for="val in getUserAttrConfig(item.typeValue)?.valueOptions"
                        :key="val.value"
                        :label="val.label"
                        :value="val.value"
                      ></el-option>
                    </el-select>

                    <!-- 留存用户的特殊输入框 -->
                    <div v-if="item.typeValue === 'retention_user'" class="retention-input-wrapper">
                      <el-input-number
                        v-model="item.value"
                        :min="1"
                        :disabled="isViewMode"
                        controls-position="right"
                        class="retention-input-number"
                      ></el-input-number>
                      <span class="retention-text">留</span>
                    </div>
                  </div>
                  <el-button v-if="!isViewMode" type="text" class="delete-btn" @click="deleteUserAttribute(index)">
                    <i class="el-icon-error"></i>
                  </el-button>
                </div>
              </div>
            </div>
          </div>
          <el-button v-if="!isViewMode" type="text" class="add-btn" @click="addUserAttribute">
            <i class="el-icon-circle-plus-outline"></i>
            添加
          </el-button>
        </div>
      </div>

      <!-- 行为条件 -->
      <div class="condition-block">
        <div class="condition-header">
          <span class="condition-label">行为条件：</span>
        </div>
        <div class="condition-content">
          <div class="condition-list-wrapper" v-if="behaviorConditions.length > 0">
            <div
              class="condition-badge-center"
              :class="{ clickable: !isViewMode }"
              @click="!isViewMode && toggleBehaviorLogic()"
            >
              {{ behaviorLogicType === 'and' ? '且' : '或' }}
            </div>
            <div class="condition-list">
              <div v-for="(item, index) in behaviorConditions" :key="'behavior-' + index" class="condition-item">
                <div class="condition-row">
                  <div class="condition-selects">
                    <el-select
                      v-model="item.timeDimensionValue"
                      placeholder="请选择"
                      class="condition-select"
                      :disabled="isViewMode"
                    >
                      <el-option
                        v-for="time in timeDimensions"
                        :key="time.value"
                        :label="time.label"
                        :value="time.value"
                      ></el-option>
                    </el-select>

                    <el-select
                      v-if="!item.showCalculator"
                      v-model="item.behaviorTypeValue"
                      placeholder="请选择"
                      class="condition-select"
                      :disabled="isViewMode"
                    >
                      <el-option
                        v-for="behavior in behaviorTypes"
                        :key="behavior.value"
                        :label="behavior.label"
                        :value="behavior.value"
                      ></el-option>
                    </el-select>

                    <span v-else class="custom-text">自定义</span>

                    <!-- 累计广告收入特殊显示 -->
                    <template v-if="item.behaviorTypeValue === 'ad_usd_revenue'">
                      <span class="special-text">每达到</span>
                      <el-input-number
                        v-model="item.value"
                        :min="1"
                        :step="1"
                        :disabled="isViewMode"
                        controls-position="right"
                        class="revenue-input-number"
                      ></el-input-number>
                      <span class="special-text">美元</span>
                    </template>

                    <!-- 累计付费收入特殊显示 -->
                    <template v-else-if="item.behaviorTypeValue === 'pay_purchase_usd_revenue'">
                      <el-input-number
                        v-model="item.value"
                        :min="1"
                        :step="1"
                        :disabled="isViewMode"
                        controls-position="right"
                        class="revenue-input-number"
                      ></el-input-number>
                      <span class="special-text">美元</span>
                    </template>

                    <!-- 其他行为类型的默认显示 -->
                    <template v-else>
                      <el-select
                        v-model="item.operatorValue"
                        placeholder="请选择"
                        class="condition-select"
                        :disabled="isViewMode"
                      >
                        <el-option
                          v-for="op in operators"
                          :key="op.value"
                          :label="op.label"
                          :value="op.value"
                        ></el-option>
                      </el-select>

                      <el-input
                        v-if="item.operatorValue !== 'range'"
                        v-model="item.value"
                        placeholder="请输入"
                        class="condition-input"
                        type="number"
                        :disabled="isViewMode"
                      ></el-input>

                      <template v-if="item.operatorValue === 'range'">
                        <el-input
                          v-model="item.value"
                          placeholder="最小值"
                          class="condition-input"
                          type="number"
                          :disabled="isViewMode"
                        ></el-input>
                        <span class="range-separator">-</span>
                        <el-input
                          v-model="item.value2"
                          placeholder="最大值"
                          class="condition-input"
                          type="number"
                          :disabled="isViewMode"
                        ></el-input>
                      </template>
                    </template>
                    <!-- 操作按钮 -->
                    <svg-icon
                      v-if="!['ad_usd_revenue', 'pay_purchase_usd_revenue'].includes(item.behaviorTypeValue)"
                      icon-class="calculator"
                      class="opera-icon"
                      :class="{ 'active-icon': item.showCalculator }"
                      @click.native="toggleCalculator(index)"
                    ></svg-icon>
                    <svg-icon
                      icon-class="subProject"
                      class="opera-icon"
                      :class="{ 'active-icon': item.showSubProject }"
                      @click.native="toggleSubProject(index)"
                    ></svg-icon>
                  </div>
                  <el-button v-if="!isViewMode" type="text" class="delete-btn" @click="deleteBehaviorCondition(index)">
                    <i class="el-icon-error"></i>
                  </el-button>
                </div>

                <!-- 计算器面板 -->
                <div v-if="item.showCalculator" class="calculator-panel">
                  <div class="calculator-header">公式</div>
                  <div class="calculator-buttons">
                    <div class="button-row operator-row">
                      <el-popover
                        v-model="item.showEventPopover"
                        placement="bottom-start"
                        width="200"
                        trigger="click"
                        popper-class="event-selector-popover"
                      >
                        <div class="event-selector-list">
                          <div
                            v-for="behavior in behaviorTypes.filter(
                              item => !['ad_usd_revenue', 'pay_purchase_usd_revenue'].includes(item.value),
                            )"
                            :key="behavior.value"
                            class="event-selector-item"
                            @click="insertEventConfirm(index, behavior.value)"
                          >
                            {{ behavior.label }}
                          </div>
                        </div>
                        <button slot="reference" class="calc-btn insert-event-btn">
                          <i class="el-icon-plus"></i>
                          数据指标
                        </button>
                      </el-popover>
                      <button
                        v-for="operator in ['+', '-', '×', '÷', '(', ')']"
                        :key="operator"
                        class="calc-btn operator-btn"
                        @click="addCalculatorOperator(index, operator)"
                      >
                        {{ operator }}
                      </button>
                    </div>
                  </div>
                  <div class="calculator-display-container" @click="handleCalculatorContainerClick($event, index)">
                    <div class="calculator-display-tags">
                      <template v-for="(token, tokenIndex) in item.calculatorTokens || []">
                        <!-- 在每个 token 之前判断是否需要插入 input -->
                        <input
                          v-if="item.calculatorCursorPosition === tokenIndex"
                          ref="calculatorInput"
                          :key="`${index}-input-${tokenIndex}`"
                          :data-index="index"
                          v-model="item.calculatorInputValue"
                          :disabled="isViewMode"
                          class="calculator-input"
                          type="text"
                          placeholder=""
                          :style="{ width: getInputWidth(item.calculatorInputValue) }"
                          @input="$forceUpdate()"
                          @keydown="handleCalculatorInputKeydown($event, index)"
                          @blur="saveCalculatorInputValue(index)"
                        />
                        <el-tag
                          v-if="token.type === 'metric' || token.type === 'operator'"
                          :key="`${index}-${tokenIndex}`"
                          :data-token-index="tokenIndex"
                          type="info"
                          @close="removeCalculatorToken(index, tokenIndex)"
                          class="calculator-tag"
                        >
                          {{ token.label }}
                        </el-tag>
                        <span
                          v-else
                          :key="`${index}-${tokenIndex}-text`"
                          :data-token-index="tokenIndex"
                          class="calculator-text"
                        >
                          {{ token.value }}
                        </span>
                      </template>
                      <!-- 在最后也需要判断是否插入 input -->
                      <input
                        v-if="
                          !item.calculatorTokens ||
                          item.calculatorTokens.length === 0 ||
                          item.calculatorCursorPosition === (item.calculatorTokens || []).length
                        "
                        ref="calculatorInput"
                        :key="`${index}-input-end`"
                        :data-index="index"
                        v-model="item.calculatorInputValue"
                        :disabled="isViewMode"
                        class="calculator-input"
                        type="text"
                        placeholder=""
                        :style="{ width: getInputWidth(item.calculatorInputValue) }"
                        @input="$forceUpdate()"
                        @keydown="handleCalculatorInputKeydown($event, index)"
                        @blur="saveCalculatorInputValue(index)"
                      />
                    </div>
                  </div>
                </div>

                <!-- 子集模式面板 -->
                <div v-if="item.showSubProject" class="subproject-panel">
                  <div class="condition-content">
                    <div class="condition-list-wrapper" v-if="item.subConditions && item.subConditions.length > 0">
                      <div
                        class="condition-badge-center"
                        :class="{ clickable: !isViewMode }"
                        @click="!isViewMode && toggleSubConditionLogic(index)"
                      >
                        {{ item.subConditionLogicType === 'and' ? '且' : '或' }}
                      </div>
                      <div class="condition-list">
                        <div
                          v-for="(subItem, subIndex) in item.subConditions"
                          :key="'sub-' + subIndex"
                          class="condition-item"
                        >
                          <div class="condition-row">
                            <div class="condition-selects">
                              <el-select
                                v-model="subItem.behaviorTypeValue"
                                placeholder="请选择"
                                class="condition-select"
                                :disabled="isViewMode"
                              >
                                <el-option
                                  v-for="behavior in behaviorTypes"
                                  :key="behavior.value"
                                  :label="behavior.label"
                                  :value="behavior.value"
                                ></el-option>
                              </el-select>

                              <!-- 累计广告收入特殊显示 -->
                              <template v-if="subItem.behaviorTypeValue === 'ad_usd_revenue'">
                                <span class="special-text">每达到</span>
                                <el-input-number
                                  v-model="subItem.value"
                                  :min="1"
                                  :step="1"
                                  :disabled="isViewMode"
                                  controls-position="right"
                                  class="revenue-input-number"
                                ></el-input-number>
                                <span class="special-text">美元</span>
                              </template>

                              <!-- 累计付费收入特殊显示 -->
                              <template v-else-if="subItem.behaviorTypeValue === 'pay_purchase_usd_revenue'">
                                <el-input-number
                                  v-model="subItem.value"
                                  :min="1"
                                  :step="1"
                                  :disabled="isViewMode"
                                  controls-position="right"
                                  class="revenue-input-number"
                                ></el-input-number>
                                <span class="special-text">美元</span>
                              </template>

                              <!-- 其他行为类型的默认显示 -->
                              <template v-else>
                                <el-select
                                  v-model="subItem.operatorValue"
                                  placeholder="请选择"
                                  class="condition-select"
                                  :disabled="isViewMode"
                                >
                                  <el-option
                                    v-for="op in operators"
                                    :key="op.value"
                                    :label="op.label"
                                    :value="op.value"
                                  ></el-option>
                                </el-select>

                                <el-input
                                  v-if="subItem.operatorValue !== 'range'"
                                  v-model="subItem.value"
                                  placeholder="请输入"
                                  class="condition-input"
                                  type="number"
                                  :disabled="isViewMode"
                                ></el-input>

                                <template v-if="subItem.operatorValue === 'range'">
                                  <el-input
                                    v-model="subItem.value"
                                    placeholder="最小值"
                                    class="condition-input"
                                    type="number"
                                    :disabled="isViewMode"
                                  ></el-input>
                                  <span class="range-separator">-</span>
                                  <el-input
                                    v-model="subItem.value2"
                                    placeholder="最大值"
                                    class="condition-input"
                                    type="number"
                                    :disabled="isViewMode"
                                  ></el-input>
                                </template>
                              </template>
                            </div>
                            <el-button
                              v-if="!isViewMode"
                              type="text"
                              class="delete-btn"
                              @click="deleteSubCondition(index, subIndex)"
                            >
                              <i class="el-icon-error"></i>
                            </el-button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <el-button v-if="!isViewMode" type="text" class="add-btn" @click="addSubCondition(index)">
                      <i class="el-icon-circle-plus-outline"></i>
                      添加
                    </el-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <el-button v-if="!isViewMode" type="text" class="add-btn" @click="addBehaviorCondition">
            <i class="el-icon-circle-plus-outline"></i>
            添加
          </el-button>
        </div>
      </div>
    </div>

    <!-- 底部按钮 -->
    <div class="footer-btns">
      <el-button @click="handleSaveAndBack">返回列表</el-button>
      <div class="footer-btns-right">
        <el-button v-if="!isViewMode" @click="handleBack('cancel')">取消</el-button>
        <el-button v-if="!isViewMode" type="primary" @click="handleSave" v-loading="saveLoading">保存配置</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import SvgIcon from '@/components/SvgIcon.vue'

import {
  getBuyEventEnumAll,
  getBuyEventDetail,
  saveBuyEvent,
  saveBuyEventDraft,
  getBuyEventDraft,
  deleteBuyEventDraft,
} from '@/api/eventManager'
import { getCountryListFetch, getmediaSourceFetch } from '@/api/api'

export default {
  name: 'EventConfig',
  components: {
    SvgIcon,
  },
  data() {
    return {
      eventTitle: '新增自定义事件',
      mode: 'add', // add, edit, view, copy
      formData: {
        eventName: '',
        products: [],
        remark: '',
      },
      productList: [],
      // 用户属性条件
      userAttributes: [],
      userLogicType: 'and', // 用户属性逻辑关系
      // 行为条件
      behaviorConditions: [],
      behaviorLogicType: 'and', // 行为条件逻辑关系
      // 配置选项
      userAttributeTypes: [],
      timeDimensions: [],
      behaviorTypes: [],
      operators: [],
      countryList: [], // 国家列表
      mediaSourceList: [], // 渠道列表
      saveLoading: false,
      enumLoading: false,
    }
  },

  computed: {
    isViewMode() {
      return this.mode === 'view'
    },
    eventNameRules() {
      return [
        { required: true, message: '事件名称不能为空', trigger: 'blur' },
        { max: 40, message: '事件名称不得超过40个字符', trigger: 'blur' },
        {
          pattern: /^[a-zA-Z][a-zA-Z0-9_]*$/,
          message: '事件名称只能包含字母、数字和下划线，且必须以字母开头',
          trigger: 'blur',
        },
      ]
    },
  },

  async mounted() {
    // 加载枚举数据
    await this.loadEnumData()

    // 根据路由参数初始化页面
    this.mode = this.$route.query.mode || 'add'
    const id = this.$route.query.id

    if (this.mode === 'add') {
      this.eventTitle = '新增自定义事件'
      // 获取草稿
      await this.loadDraftData()
    } else if (this.mode === 'edit' && id) {
      await this.loadEventData(id)
      this.eventTitle = `${this.formData.eventName}`
    } else if (this.mode === 'view' && id) {
      await this.loadEventData(id)
      this.eventTitle = `${this.formData.eventName}`
    } else if (this.mode === 'copy' && id) {
      await this.loadEventData(id)
      this.eventTitle = `${this.formData.eventName}`
    }
  },

  methods: {
    // 计算 input 宽度
    getInputWidth(value) {
      if (!value || value.length === 0) {
        return '4px' // 空值时的最小宽度
      }
      // 根据内容长度动态计算宽度，每个字符约 8-14px
      const width = Math.max(20, Math.min(300, value.length * 10 + 10))
      return `${width}px`
    },

    // 处理计算器容器点击事件 - 根据点击位置移动光标
    handleCalculatorContainerClick(event, index) {
      if (this.isViewMode) return

      // 在移动光标之前，先保存input中的内容
      this.saveCalculatorInputValue(index)

      const container = event.currentTarget
      const clickX = event.clientX
      const clickY = event.clientY

      // 获取所有的 token 元素
      const tokens = container.querySelectorAll('.calculator-tag, .calculator-text')

      if (tokens.length === 0) {
        // 没有 token，光标在最后
        this.$set(this.behaviorConditions[index], 'calculatorCursorPosition', 0)
        this.focusCalculatorInput(index)
        return
      }

      let closestTokenIndex = -1
      let closestDistance = Infinity
      let insertBefore = false

      // 遍历所有 token，找到最接近点击位置的 token
      tokens.forEach((token, idx) => {
        const rect = token.getBoundingClientRect()
        const tokenCenterX = rect.left + rect.width / 2
        const tokenCenterY = rect.top + rect.height / 2

        // 计算点击位置到 token 中心的距离
        const distance = Math.sqrt(Math.pow(clickX - tokenCenterX, 2) + Math.pow(clickY - tokenCenterY, 2))

        if (distance < closestDistance) {
          closestDistance = distance
          closestTokenIndex = idx
          // 判断点击是在 token 的左边还是右边
          insertBefore = clickX < tokenCenterX
        }
      })

      if (closestTokenIndex !== -1) {
        const tokenElement = tokens[closestTokenIndex]
        const tokenIndex = parseInt(tokenElement.getAttribute('data-token-index') || closestTokenIndex)

        // 根据点击位置决定光标位置
        if (insertBefore) {
          // 点击在 token 左边，光标应该在这个 token 之前
          this.$set(this.behaviorConditions[index], 'calculatorCursorPosition', tokenIndex)
        } else {
          // 点击在 token 右边，光标应该在这个 token 之后
          this.$set(this.behaviorConditions[index], 'calculatorCursorPosition', tokenIndex + 1)
        }
      } else {
        // 如果没有找到，默认在最后
        this.$set(
          this.behaviorConditions[index],
          'calculatorCursorPosition',
          (this.behaviorConditions[index].calculatorTokens || []).length,
        )
      }

      // 聚焦输入框
      this.focusCalculatorInput(index)
    },

    // 聚焦计算器输入框
    focusCalculatorInput(index) {
      this.$nextTick(() => {
        const inputs = this.$refs.calculatorInput
        if (inputs && inputs.length > 0) {
          const targetInput = Array.from(inputs).find(input => input.dataset.index == index)
          if (targetInput) {
            targetInput.focus()
          }
        }
      })
    },

    // 保存计算器输入框的内容到tokens
    saveCalculatorInputValue(index) {
      const inputValue = this.behaviorConditions[index].calculatorInputValue
      if (inputValue && inputValue.trim()) {
        const cursorPos = this.behaviorConditions[index].calculatorCursorPosition || 0
        const tokens = this.behaviorConditions[index].calculatorTokens || []

        // 在当前光标位置插入文本token
        tokens.splice(cursorPos, 0, {
          type: 'text',
          value: inputValue.trim(),
        })
        this.$set(this.behaviorConditions[index], 'calculatorTokens', tokens)
        this.$set(this.behaviorConditions[index], 'calculatorInputValue', '')

        // 光标位置后移一位
        this.$set(this.behaviorConditions[index], 'calculatorCursorPosition', cursorPos + 1)

        // 更新显示文本
        this.updateCalculatorText(index)
      }
    },

    // 移除计算器 token
    removeCalculatorToken(index, tokenIndex) {
      const tokens = this.behaviorConditions[index].calculatorTokens || []
      tokens.splice(tokenIndex, 1)
      this.$set(this.behaviorConditions[index], 'calculatorTokens', tokens)

      // 更新光标位置
      const cursorPos = this.behaviorConditions[index].calculatorCursorPosition
      if (cursorPos > tokenIndex) {
        this.$set(this.behaviorConditions[index], 'calculatorCursorPosition', cursorPos - 1)
      }

      // 更新显示文本用于保存
      this.updateCalculatorText(index)
    },

    // 处理计算器输入框的键盘事件
    handleCalculatorInputKeydown(event, index) {
      const input = event.target
      const value = input.value.trim()
      const cursorPos = this.behaviorConditions[index].calculatorCursorPosition || 0

      // 处理 Backspace 键 - 当输入框为空时，删除光标前的 token
      if (event.key === 'Backspace' && !value) {
        event.preventDefault()
        const tokens = this.behaviorConditions[index].calculatorTokens || []
        if (tokens.length > 0 && cursorPos > 0) {
          // 删除光标前面的 token
          tokens.splice(cursorPos - 1, 1)
          this.$set(this.behaviorConditions[index], 'calculatorTokens', tokens)
          // 光标位置前移
          this.$set(this.behaviorConditions[index], 'calculatorCursorPosition', cursorPos - 1)
          this.updateCalculatorText(index)
        }
      }

      // 处理左右箭头键 - 移动光标位置
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        // 先保存输入框内容
        this.saveCalculatorInputValue(index)
        if (cursorPos > 0) {
          this.$set(this.behaviorConditions[index], 'calculatorCursorPosition', cursorPos - 1)
          this.focusCalculatorInput(index)
        }
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        // 先保存输入框内容
        this.saveCalculatorInputValue(index)
        const tokens = this.behaviorConditions[index].calculatorTokens || []
        if (cursorPos < tokens.length) {
          this.$set(this.behaviorConditions[index], 'calculatorCursorPosition', cursorPos + 1)
          this.focusCalculatorInput(index)
        }
      }
    },

    // 更新计算器文本（用于保存）- 不包含输入框内容
    updateCalculatorText(index, includeInput = false) {
      // 如果需要包含输入框内容，则先保存输入框中的文本到 tokens
      if (includeInput) {
        const inputValue = this.behaviorConditions[index].calculatorInputValue
        if (inputValue && inputValue.trim()) {
          const tokens = this.behaviorConditions[index].calculatorTokens || []
          tokens.push({
            type: 'text',
            value: inputValue.trim(),
          })
          this.$set(this.behaviorConditions[index], 'calculatorTokens', tokens)
          this.$set(this.behaviorConditions[index], 'calculatorInputValue', '')
        }
      }

      const tokens = this.behaviorConditions[index].calculatorTokens || []
      let text = ''
      tokens.forEach(token => {
        if (token.type === 'metric') {
          text += `{${token.label}}`
        } else if (token.type === 'operator') {
          text += token.value
        } else {
          text += token.value
        }
      })
      this.$set(this.behaviorConditions[index], 'showCalculatorText', text)
    },

    addCalculatorValue(index, value) {
      this.behaviorConditions[index].showCalculatorText += value
    },
    addCalculatorOperator(index, operator) {
      let cursorPos = this.behaviorConditions[index].calculatorCursorPosition
      const tokens = [...(this.behaviorConditions[index].calculatorTokens || [])]

      // 先保存输入框中的文本
      const inputValue = this.behaviorConditions[index].calculatorInputValue
      if (inputValue && inputValue.trim()) {
        const insertPos = cursorPos !== undefined ? cursorPos : tokens.length
        tokens.splice(insertPos, 0, {
          type: 'text',
          value: inputValue.trim(),
        })
        // 光标位置后移
        cursorPos = insertPos + 1
      }

      // 将显示符号转换为实际运算符
      const operatorMap = {
        '×': '*',
        '÷': '/',
      }
      const actualOperator = operatorMap[operator] || operator

      // 在光标位置添加运算符 token
      const finalCursorPos = cursorPos !== undefined ? cursorPos : tokens.length
      tokens.splice(finalCursorPos, 0, {
        type: 'operator',
        value: actualOperator,
        label: operator,
      })

      // 批量更新所有状态，减少渲染次数
      this.$set(this.behaviorConditions[index], 'calculatorTokens', tokens)
      this.$set(this.behaviorConditions[index], 'calculatorInputValue', '')
      this.$set(this.behaviorConditions[index], 'calculatorCursorPosition', finalCursorPos + 1)

      this.updateCalculatorText(index)

      // 聚焦输入框
      this.focusCalculatorInput(index)
    },
    clearCalculator(index) {
      const behavior = this.behaviorTypes.find(item => item.value === this.behaviorConditions[index].behaviorTypeValue)
      if (behavior) {
        this.$set(this.behaviorConditions[index], 'calculatorTokens', [
          {
            type: 'metric',
            value: behavior.value,
            label: behavior.label,
          },
        ])
        this.$set(this.behaviorConditions[index], 'calculatorInputValue', '')
        this.updateCalculatorText(index)
      }
    },
    insertEventConfirm(index, eventValue) {
      const behavior = this.behaviorTypes.find(item => item.value === eventValue)
      if (behavior) {
        let cursorPos = this.behaviorConditions[index].calculatorCursorPosition
        const tokens = [...(this.behaviorConditions[index].calculatorTokens || [])]

        // 先保存输入框中的文本
        const inputValue = this.behaviorConditions[index].calculatorInputValue
        if (inputValue && inputValue.trim()) {
          const insertPos = cursorPos !== undefined ? cursorPos : tokens.length
          tokens.splice(insertPos, 0, {
            type: 'text',
            value: inputValue.trim(),
          })
          // 光标位置后移
          cursorPos = insertPos + 1
        }

        // 在光标位置添加数据指标 token
        const finalCursorPos = cursorPos !== undefined ? cursorPos : tokens.length
        tokens.splice(finalCursorPos, 0, {
          type: 'metric',
          value: behavior.value,
          label: behavior.label,
        })

        // 批量更新所有状态，减少渲染次数
        this.$set(this.behaviorConditions[index], 'calculatorTokens', tokens)
        this.$set(this.behaviorConditions[index], 'calculatorInputValue', '')
        this.$set(this.behaviorConditions[index], 'calculatorCursorPosition', finalCursorPos + 1)
        this.$set(this.behaviorConditions[index], 'showEventPopover', false)

        this.updateCalculatorText(index)

        // 聚焦输入框
        this.focusCalculatorInput(index)
      }
    },
    // deleteCalculatorValue(index) {
    //   this.behaviorConditions[index].showCalculatorText = this.behaviorConditions[index].showCalculatorText.slice(0, -1)
    // },
    toggleSubProject(index) {
      const item = this.behaviorConditions[index]
      const isShow = !item.showSubProject
      this.$set(this.behaviorConditions[index], 'showSubProject', isShow)

      // 初始化子集数据
      if (isShow && (!item.subConditions || item.subConditions.length === 0)) {
        this.$set(this.behaviorConditions[index], 'subConditions', [])
        this.$set(this.behaviorConditions[index], 'subConditionLogicType', 'and')
        this.addSubCondition(index)
      } else {
        this.$set(this.behaviorConditions[index], 'subConditions', [])
        this.$set(this.behaviorConditions[index], 'subConditionLogicType', 'and')
      }
    },

    // 切换子集逻辑关系
    toggleSubConditionLogic(index) {
      const item = this.behaviorConditions[index]
      const newLogicType = item.subConditionLogicType === 'and' ? 'or' : 'and'
      this.$set(this.behaviorConditions[index], 'subConditionLogicType', newLogicType)
    },

    // 添加子集条件
    addSubCondition(index) {
      const item = this.behaviorConditions[index]

      // 校验已有的子集条件是否填写完整
      if (item.subConditions && item.subConditions.length > 0) {
        for (let i = 0; i < item.subConditions.length; i++) {
          const subItem = item.subConditions[i]

          if (!subItem.behaviorTypeValue) {
            this.$message.warning(`请先完善第${i + 1}条子集条件的行为类型`)
            return
          }

          // 累计收入类型不需要比较关系
          const isRevenueType = ['ad_usd_revenue', 'pay_purchase_usd_revenue'].includes(subItem.behaviorTypeValue)
          if (!isRevenueType && !subItem.operatorValue) {
            this.$message.warning(`请先完善第${i + 1}条子集条件的比较关系`)
            return
          }

          if (!subItem.value && subItem.value !== 0) {
            this.$message.warning(`请先完善第${i + 1}条子集条件的阈值`)
            return
          }

          if (subItem.operatorValue === 'range' && !subItem.value2) {
            this.$message.warning(`请先完善第${i + 1}条子集条件的区间最大值`)
            return
          }
        }
      }

      // 添加新的子集条件
      if (!item.subConditions) {
        this.$set(this.behaviorConditions[index], 'subConditions', [])
      }

      item.subConditions.push({
        behaviorTypeValue: '',
        operatorValue: '',
        value: '',
        value2: '',
      })
    },

    // 删除子集条件
    deleteSubCondition(index, subIndex) {
      this.$confirm('确定要删除这个子集条件吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => {
          this.behaviorConditions[index].subConditions.splice(subIndex, 1)
          this.$message.success('删除成功')
        })
        .catch(() => {})
    },
    // 加载枚举数据
    async loadEnumData() {
      try {
        this.enumLoading = true

        // 并行加载所有枚举数据
        const [enumRes, countryRes, mediaSourceRes] = await Promise.all([
          getBuyEventEnumAll(),
          getCountryListFetch({}),
          getmediaSourceFetch({}),
        ])

        // 处理国家列表
        if (countryRes.code === '0000' && countryRes.countryList) {
          this.countryList = (countryRes.countryList || []).map(item => ({
            label: item.name,
            value: item.paramGeo || item.geo,
          }))
        }

        // 处理渠道列表
        if (mediaSourceRes.code === '0000' && mediaSourceRes.mediaList) {
          this.mediaSourceList = (mediaSourceRes.mediaList || []).map(item => ({
            label: item, // 直接使用渠道名称作为显示
            value: item, // 直接使用渠道名称作为值
          }))
        }

        const res = enumRes
        if (res.code === '0000' && res.data) {
          // 处理用户属性类型 - 转换格式 code/desc -> value/label
          const attributeTypes = res.data.attributeType || []
          this.userAttributeTypes = attributeTypes.map(item => {
            const config = {
              label: item.desc,
              value: item.code,
              hasOperator: false,
              hasValue: false,
            }

            // 判断哪些属性需要操作符和值
            if (item.code === 'country_code') {
              config.hasOperator = true
              config.hasValue = true
              config.operators = [
                { label: '等于', value: 'equals' },
                { label: '不等于', value: 'not_equals' },
              ]
              config.valueType = 'select'
              // 使用从接口获取的国家列表
              config.valueOptions = this.countryList
            } else if (item.code === 'media_source') {
              config.hasOperator = true
              config.hasValue = true
              config.operators = [
                { label: '等于', value: 'equals' },
                { label: '不等于', value: 'not_equals' },
              ]
              config.valueType = 'select'
              // 使用从接口获取的渠道列表
              config.valueOptions = this.mediaSourceList
            }

            return config
          })

          // 处理时间维度 - 转换格式
          const timeDimensions = res.data.timeDimension || []
          this.timeDimensions = timeDimensions.map(item => ({
            label: item.desc,
            value: item.code,
          }))

          // 处理行为类型 - 转换格式并保留 category
          const behaviorTypes = res.data.behaviorType || []
          this.behaviorTypes = behaviorTypes.map(item => ({
            label: item.desc,
            value: item.code,
            category: item.category,
          }))

          // 处理比较操作符 - 转换格式
          const comparisonOperators = res.data.comparisonOperator || []
          this.operators = comparisonOperators.map(item => ({
            label: item.desc,
            value: item.code,
          }))

          // 处理产品列表
          this.productList = res.data.products || []
        } else {
          this.$message.error(res.message || '获取枚举数据失败')
        }
      } catch (error) {
        console.error('加载枚举数据失败:', error)
        this.$message.error('获取枚举数据失败')
      } finally {
        this.enumLoading = false
      }
    },

    async loadDraftData() {
      try {
        const res = await getBuyEventDraft()
        if (res.code === '0000' && res.data) {
          console.log('[ res.data ] >', res.data)
          this.$confirm('确定要加载草稿数据吗？', '提示', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning',
          })
            .then(() => {
              // 渲染草稿数据
              this.renderDraftData(res.data)
            })
            .catch(() => {
              this.deleteDraftData()
            })
        }
      } catch (error) {
        console.error('加载草稿数据失败:', error)
      }
    },

    // 渲染草稿数据
    renderDraftData(data) {
      try {
        // 1. 回显基本信息
        this.formData = {
          eventName: data.eventName || '',
          products: data.productIds || [],
          remark: data.notes || '',
        }

        // 2. 分离用户属性和行为条件
        const userAttrConditions = []
        const behaviorConditions = []

        ;(data.conditions || []).forEach(condition => {
          if (condition.conditionType === 1) {
            // 用户属性
            userAttrConditions.push(condition)
          } else if (condition.conditionType === 2) {
            // 行为条件
            behaviorConditions.push(condition)
          }
        })

        // 3. 回显用户属性
        this.userAttributes = userAttrConditions.map(item => {
          let value = ''

          // 处理 conditionValue
          if (item.conditionValue && item.conditionValue.length > 0) {
            // 留存用户：取数组第一个值（天数）
            if (item.attributeType === 'retention_user') {
              value = item.conditionValue[0]
            } else {
              // 其他类型：直接使用（可能是单选或多选）
              value = item.conditionValue
            }
          }

          return {
            typeValue: item.attributeType || '',
            operatorValue: item.comparisonOperator || '',
            value: value,
            logicType: 'and',
          }
        })

        // 4. 回显行为条件
        this.behaviorConditions = behaviorConditions.map(item => {
          let value = ''
          let value2 = ''

          // 处理 thresholdValue
          if (item.thresholdValue) {
            const threshold = item.thresholdValue

            if (threshold.value !== undefined && threshold.value !== null) {
              // 单值类型：{"value": 2}
              value = threshold.value
            } else if (threshold.min !== undefined && threshold.max !== undefined) {
              // 区间类型：{"min": 1, "max": 10}
              value = threshold.min
              value2 = threshold.max
            }
          }

          // 处理计算器文本
          const hasCalculatorText = !!item.calculatorText
          let showCalculatorText = ''
          let calculatorTokens = []
          if (hasCalculatorText) {
            // 直接使用显示格式（后端返回的就是带花括号的格式）
            showCalculatorText = item.calculatorText
            // 解析显示格式为 tokens 数组
            calculatorTokens = this.parseDisplayTextToTokens(item.calculatorText)
          }

          // 处理子条件
          const subConditions = []
          let subConditionLogicType = 'and'
          if (item.child && item.child.conditions && item.child.conditions.length > 0) {
            subConditionLogicType = item.child.logicType || 'and'

            item.child.conditions.forEach(subItem => {
              let subValue = ''
              let subValue2 = ''

              if (subItem.thresholdValue) {
                const threshold = subItem.thresholdValue
                if (threshold.value !== undefined && threshold.value !== null) {
                  subValue = threshold.value
                } else if (threshold.min !== undefined && threshold.max !== undefined) {
                  subValue = threshold.min
                  subValue2 = threshold.max
                }
              }

              subConditions.push({
                behaviorTypeValue: subItem.behaviorType || '',
                operatorValue: subItem.comparisonOperator || '',
                value: subValue,
                value2: subValue2,
              })
            })
          }

          return {
            timeDimensionValue: item.timeDimension || '',
            behaviorTypeValue: item.behaviorType || '',
            operatorValue: item.comparisonOperator || '',
            value: value,
            value2: value2,
            logicType: '', // 稍后统一设置
            showCalculator: hasCalculatorText,
            showCalculatorText: showCalculatorText,
            calculatorTokens: calculatorTokens,
            calculatorInputValue: '',
            calculatorCursorPosition: calculatorTokens.length,
            showSubProject: subConditions.length > 0,
            showEventPopover: false,
            subConditions: subConditions,
            subConditionLogicType: subConditionLogicType,
          }
        })

        // 5. 确定行为条件的逻辑关系
        // 如果所有行为条件的 conditionGroup 相同，则为 and，否则为 or
        if (behaviorConditions.length > 0) {
          const firstGroup = behaviorConditions[0].conditionGroup
          const allSameGroup = behaviorConditions.every(item => item.conditionGroup === firstGroup)
          this.behaviorLogicType = allSameGroup ? 'and' : 'or'

          // 更新所有行为条件的 logicType
          this.behaviorConditions.forEach(item => {
            item.logicType = this.behaviorLogicType
          })
        }

        console.log('草稿数据回显完成:', {
          formData: this.formData,
          userAttributes: this.userAttributes,
          behaviorConditions: this.behaviorConditions,
          behaviorLogicType: this.behaviorLogicType,
        })

        this.$message.success('草稿数据加载成功')
      } catch (error) {
        console.error('渲染草稿数据失败:', error)
        this.$message.error('加载草稿数据失败')
      }
    },

    async deleteDraftData() {
      try {
        await deleteBuyEventDraft()
      } catch (error) {
        console.error('删除草稿数据失败:', error)
      }
    },

    // 加载事件数据
    async loadEventData(id) {
      try {
        const res = await getBuyEventDetail({ id })

        if (res.code === '0000' && res.data) {
          const data = res.data

          // 1. 转换产品数据：从对象数组转为id数组
          const productIds = (data.products || []).map(p => p.afid)

          this.formData = {
            eventName: data.eventName,
            products: productIds,
            remark: data.notes || '',
          }

          // 2. 转换用户属性数据
          this.userAttributes = (data.userAttributes || []).map(item => {
            // 解析 conditionValue，可能是 null 或数组
            let value = ''
            if (item.conditionValue) {
              try {
                const parsedValue =
                  typeof item.conditionValue === 'string' ? JSON.parse(item.conditionValue) : item.conditionValue

                // 留存用户：取数组第一个值（天数）
                if (item.attributeType === 'retention_user' && Array.isArray(parsedValue) && parsedValue.length > 0) {
                  value = parsedValue[0]
                } else {
                  value = parsedValue
                }
              } catch (e) {
                value = item.conditionValue
              }
            }

            return {
              typeValue: item.attributeType || '',
              operatorValue: item.comparisonOperator || '',
              value: value,
              logicType: 'and', // 用户属性固定为且
            }
          })

          // 3. 转换行为条件数据
          this.behaviorConditions = (data.behaviorConditions || []).map(item => {
            // 解析 thresholdValue
            let value = ''
            let value2 = ''
            if (item.thresholdValue) {
              try {
                const threshold =
                  typeof item.thresholdValue === 'string' ? JSON.parse(item.thresholdValue) : item.thresholdValue

                if ('value' in threshold) {
                  // 单值类型：{"value": 5}
                  value = threshold.value
                } else if ('min' in threshold && 'max' in threshold) {
                  // 区间类型：{"min": 1, "max": 10}
                  value = threshold.min
                  value2 = threshold.max
                }
              } catch (e) {
                console.error('解析 thresholdValue 失败:', e)
              }
            }

            // 处理计算器文本
            const hasCalculatorText = !!item.calculatorText
            let showCalculatorText = ''
            let calculatorTokens = []
            if (hasCalculatorText) {
              // 直接使用显示格式（后端返回的就是带花括号的格式）
              showCalculatorText = item.calculatorText
              // 解析显示格式为 tokens 数组
              calculatorTokens = this.parseDisplayTextToTokens(item.calculatorText)
            }

            // 处理子条件 - 从 ext 字段中解析
            const subConditions = []
            let subConditionLogicType = 'and'
            if (item.ext) {
              try {
                const extData = typeof item.ext === 'string' ? JSON.parse(item.ext) : item.ext
                if (extData && extData.conditions && extData.conditions.length > 0) {
                  subConditionLogicType = extData.logicType || 'and'

                  extData.conditions.forEach(subItem => {
                    let subValue = ''
                    let subValue2 = ''

                    if (subItem.thresholdValue) {
                      const threshold =
                        typeof subItem.thresholdValue === 'string'
                          ? JSON.parse(subItem.thresholdValue)
                          : subItem.thresholdValue

                      if (threshold.value !== undefined && threshold.value !== null) {
                        subValue = threshold.value
                      } else if (threshold.min !== undefined && threshold.max !== undefined) {
                        subValue = threshold.min
                        subValue2 = threshold.max
                      }
                    }

                    subConditions.push({
                      behaviorTypeValue: subItem.behaviorType || '',
                      operatorValue: subItem.comparisonOperator || '',
                      value: subValue,
                      value2: subValue2,
                    })
                  })
                }
              } catch (e) {
                console.error('解析 ext 字段失败:', e)
              }
            }

            return {
              timeDimensionValue: item.timeDimension || '',
              behaviorTypeValue: item.behaviorType || '',
              operatorValue: item.comparisonOperator || '',
              value: value,
              value2: value2,
              logicType: '', // 后面统一设置
              showCalculator: hasCalculatorText,
              showCalculatorText: showCalculatorText,
              calculatorTokens: calculatorTokens,
              calculatorInputValue: '',
              calculatorCursorPosition: calculatorTokens.length,
              showSubProject: subConditions.length > 0,
              showEventPopover: false,
              subConditions: subConditions,
              subConditionLogicType: subConditionLogicType,
            }
          })

          // 4. 设置逻辑关系
          // 用户属性固定为且
          this.userLogicType = 'and'

          // 根据 conditionGroup 判断行为条件的逻辑关系
          // 如果所有行为条件的 conditionGroup 相同，则为 and，否则为 or
          if (this.behaviorConditions.length > 0 && data.behaviorConditions.length > 0) {
            const firstGroup = data.behaviorConditions[0].conditionGroup
            const allSameGroup = data.behaviorConditions.every(item => item.conditionGroup === firstGroup)
            this.behaviorLogicType = allSameGroup ? 'and' : 'or'

            // 更新所有行为条件的 logicType
            this.behaviorConditions.forEach(item => {
              item.logicType = this.behaviorLogicType
            })
          }

          console.log('回显数据转换完成:', {
            formData: this.formData,
            userAttributes: this.userAttributes,
            behaviorConditions: this.behaviorConditions,
            behaviorLogicType: this.behaviorLogicType,
          })
        }
      } catch (error) {
        console.error('加载事件数据失败:', error)
        this.$message.error('获取事件详情失败')
      }
    },

    // ========== 用户属性相关 ==========
    getUserAttrConfig(typeValue) {
      return this.userAttributeTypes.find(item => item.value === typeValue)
    },

    // 获取可用的用户属性类型（过滤已选择的）
    getAvailableUserAttrTypes(currentIndex) {
      const selectedTypes = this.userAttributes
        .map((item, index) => (index !== currentIndex ? item.typeValue : null))
        .filter(Boolean)

      return this.userAttributeTypes.filter(type => !selectedTypes.includes(type.value))
    },

    onUserAttrTypeChange(item, index) {
      // 当类型改变时，检查是否重复
      const duplicateIndex = this.userAttributes.findIndex(
        (attr, idx) => idx !== index && attr.typeValue === item.typeValue && attr.typeValue,
      )

      if (duplicateIndex !== -1) {
        this.$message.warning('该用户属性已存在，请选择其他属性')
        item.typeValue = ''
        return
      }

      // 重置操作符和值
      const config = this.getUserAttrConfig(item.typeValue)
      if (config) {
        if (!config.hasOperator) {
          item.operatorValue = ''
        }
        if (!config.hasValue) {
          item.value = ''
        } else if (item.typeValue === 'retention_user') {
          // 留存用户默认值为 1
          item.value = 1
        }
      }
    },

    addUserAttribute() {
      // 校验已有的用户属性是否填写完整
      if (this.userAttributes.length > 0) {
        for (let i = 0; i < this.userAttributes.length; i++) {
          const item = this.userAttributes[i]

          if (!item.typeValue) {
            this.$message.warning(`请先完善第${i + 1}条用户属性的类型`)
            return
          }

          const config = this.getUserAttrConfig(item.typeValue)
          if (config) {
            if (config.hasOperator && !item.operatorValue) {
              this.$message.warning(`请先完善第${i + 1}条用户属性的比较关系`)
              return
            }
            if (config.hasValue && !item.value && item.value !== 0) {
              this.$message.warning(`请先完善第${i + 1}条用户属性的值`)
              return
            }
          }
        }
      }

      // 直接添加一个新的空白用户属性行
      this.userAttributes.push({
        typeValue: '',
        operatorValue: '',
        value: '',
        logicType: 'and', // 用户属性固定为且
      })
    },

    deleteUserAttribute(index) {
      // 删除用户属性条件（需二次确认）
      this.$confirm('确定要删除这个用户属性条件吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => {
          this.userAttributes.splice(index, 1)
          this.$message.success('删除成功')
        })
        .catch(() => {})
    },

    // ========== 行为条件相关 ==========
    addBehaviorCondition() {
      // 校验已有的行为条件是否填写完整
      if (this.behaviorConditions.length > 0) {
        for (let i = 0; i < this.behaviorConditions.length; i++) {
          const item = this.behaviorConditions[i]

          //   if (!item.timeDimensionValue) {
          //     this.$message.warning(`请先完善第${i + 1}条行为条件的时间维度`)
          //     return
          //   }

          if (!item.showCalculator && !item.behaviorTypeValue) {
            this.$message.warning(`请先完善第${i + 1}条行为条件的行为类型`)
            return
          }

          // 累计收入类型不需要比较关系
          const isRevenueType = ['ad_usd_revenue', 'pay_purchase_usd_revenue'].includes(item.behaviorTypeValue)
          if (!isRevenueType && !item.operatorValue) {
            this.$message.warning(`请先完善第${i + 1}条行为条件的比较关系`)
            return
          }

          if (!item.value && item.value !== 0) {
            this.$message.warning(`请先完善第${i + 1}条行为条件的阈值`)
            return
          }

          if (item.operatorValue === 'range' && !item.value2) {
            this.$message.warning(`请先完善第${i + 1}条行为条件的区间最大值`)
            return
          }
        }
      }

      // 直接添加一个新的空白行为条件行
      this.behaviorConditions.push({
        timeDimensionValue: '',
        behaviorTypeValue: '',
        operatorValue: '',
        value: '',
        value2: '',
        logicType: this.behaviorLogicType,
        showCalculator: false,
        showSubProject: false,
        showEventPopover: false,
        calculatorTokens: [],
        calculatorInputValue: '',
        calculatorCursorPosition: 0,
        subConditions: [],
        subConditionLogicType: 'and',
      })
    },

    deleteBehaviorCondition(index) {
      // 删除行为条件（需二次确认）
      this.$confirm('确定要删除这个行为条件吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => {
          this.behaviorConditions.splice(index, 1)
          this.$message.success('删除成功')
        })
        .catch(() => {})
    },

    toggleBehaviorLogic() {
      // 切换行为条件逻辑关系
      this.behaviorLogicType = this.behaviorLogicType === 'and' ? 'or' : 'and'
      // 更新所有行为条件的逻辑类型
      this.behaviorConditions.forEach(item => {
        item.logicType = this.behaviorLogicType
      })
    },

    toggleCalculator(index) {
      this.$set(this.behaviorConditions[index], 'showCalculator', !this.behaviorConditions[index].showCalculator)
      if (this.behaviorConditions[index].showCalculator) {
        const behavior = this.behaviorTypes.find(
          item => item.value === this.behaviorConditions[index].behaviorTypeValue,
        )
        if (behavior) {
          this.$set(this.behaviorConditions[index], 'calculatorTokens', [
            {
              type: 'metric',
              value: behavior.value,
              label: behavior.label,
            },
          ])
          this.$set(this.behaviorConditions[index], 'calculatorInputValue', '')
          // 初始化光标位置在最后
          this.$set(this.behaviorConditions[index], 'calculatorCursorPosition', 1)
          this.updateCalculatorText(index)
        }
      } else {
        this.$set(this.behaviorConditions[index], 'calculatorTokens', [])
        this.$set(this.behaviorConditions[index], 'calculatorInputValue', '')
        this.$set(this.behaviorConditions[index], 'showCalculatorText', '')
        this.$set(this.behaviorConditions[index], 'calculatorCursorPosition', 0)
      }
    },

    // ========== 保存和返回 ==========
    // 重置表单数据
    resetFormData() {
      // 重置基本信息
      this.formData = {
        eventName: '',
        products: [],
        remark: '',
      }

      // 重置用户属性
      this.userAttributes = []
      this.userLogicType = 'and'

      // 重置行为条件
      this.behaviorConditions = []
      this.behaviorLogicType = 'and'

      // 重置标题
      this.eventTitle = '新增自定义事件'

      // 重置模式
      this.mode = 'add'

      // 重置加载状态
      this.saveLoading = false
      this.enumLoading = false

      // 清空表单验证
      this.$nextTick(() => {
        if (this.$refs.formRef) {
          this.$refs.formRef.clearValidate()
        }
      })
    },

    handleBack(type) {
      if (this.mode === 'add' && type === 'cancel') this.deleteDraftData()
      // 重置表单数据，确保不会相互影响
      this.resetFormData()
      // 返回列表页
      this.$router.push({ name: 'AcquisitionEventManagement' })
    },

    isChanged() {
      return (
        this.formData.eventName !== '' ||
        this.formData.products.length > 0 ||
        this.formData.remark !== '' ||
        this.userAttributes.length > 0 ||
        this.behaviorConditions.length > 0
      )
    },

    async handleSaveAndBack() {
      if (this.mode === 'add' && this.isChanged()) {
        try {
          const res = await saveBuyEventDraft(this.formParams())
          if (res.code === '0000') {
            this.$message.success('保存成功')
            this.handleBack()
          }
        } catch (err) {
          console.error('保存并返回失败:', err)
        }
      } else {
        this.handleBack()
      }
    },

    // 构建传参方法
    formParams() {
      // 保存所有计算器输入框中的文本到 tokens
      this.behaviorConditions.forEach((item, index) => {
        if (item.showCalculator) {
          // 更新显示文本，包含输入框内容
          this.updateCalculatorText(index, true)
        }
      })

      const conditions = []
      let sortOrder = 0

      // 1. 处理用户属性条件（conditionType = 1, conditionGroup = 0）
      this.userAttributes.forEach(item => {
        // 根据属性类型决定 conditionValue 的格式
        let conditionValue = []
        if (item.typeValue === 'retention_user') {
          // 留存用户：保存天数（单个数值）
          conditionValue = item.value ? [item.value] : []
        } else if (item.value) {
          // 其他类型：保持原有逻辑
          conditionValue = item.value
        }

        conditions.push({
          attributeType: item.typeValue || '', // new_user, active_user, country_code, media_source, retention_user
          behaviorType: '', // 用户属性时为空
          comparisonOperator: item.operatorValue || '', // equals, not_equals
          conditionGroup: 0, // 用户属性固定为组0（且关系）
          conditionType: 1, // 1-用户属性
          conditionValue: conditionValue, // 数组格式
          sortOrder: sortOrder++,
          thresholdValue: {}, // 用户属性不使用阈值
          timeDimension: '', // 用户属性不使用时间维度
        })
      })

      // 2. 处理行为条件（conditionType = 2）
      // conditionGroup根据逻辑关系决定：and时都在同一组，or时每个条件独立分组
      const isAndLogic = this.behaviorLogicType === 'and'
      this.behaviorConditions.forEach((item, index) => {
        // 判断是否为累计收入类型
        const isRevenueType = ['ad_usd_revenue', 'pay_purchase_usd_revenue'].includes(item.behaviorTypeValue)

        // 构建行为条件对象
        const behaviorCondition = {
          attributeType: '', // 行为条件时为空
          behaviorType: item.behaviorTypeValue || '', // level_complete, ad_revenue等
          comparisonOperator: isRevenueType ? 'gte' : item.operatorValue || '', // 累计收入类型使用 gte（大于等于）
          conditionGroup: isAndLogic ? 1 : index + 2, // and时都是组1，or时每个条件独立分组（2,3,4...）
          conditionType: 2, // 2-行为条件
          conditionValue: [], // 行为条件不使用conditionValue
          sortOrder: sortOrder++,
          thresholdValue: this.buildThresholdValue(item), // 根据操作符构建阈值
          timeDimension: item.timeDimensionValue || '', // 3日内，7日内等
        }

        // 添加 calculatorText（如果存在）
        if (item.showCalculator) {
          // 将显示格式 {事件名称} 转换为纯文本格式（去掉花括号，保留事件参数）
          behaviorCondition.calculatorText = this.convertDisplayToCalculatorText(item.showCalculatorText)
        }

        // 添加子集条件（如果存在）
        if (item.subConditions && item.subConditions.length > 0) {
          const childConditions = []

          item.subConditions.forEach(subItem => {
            const isSubRevenueType = ['ad_usd_revenue', 'pay_purchase_usd_revenue'].includes(subItem.behaviorTypeValue)

            childConditions.push({
              attributeType: '',
              behaviorType: subItem.behaviorTypeValue || '',
              comparisonOperator: isSubRevenueType ? 'gte' : subItem.operatorValue || '',
              conditionType: 2,
              conditionValue: [],
              thresholdValue: this.buildThresholdValue(subItem),
              timeDimension: item.timeDimensionValue || '', // 子集使用父条件的时间维度
            })
          })

          // 添加 child 字段，包含子集条件和逻辑类型
          behaviorCondition.child = {
            logicType: item.subConditionLogicType || 'and', // 子集的逻辑类型（and/or）
            conditions: childConditions,
          }
        }

        conditions.push(behaviorCondition)
      })

      if (this.mode === 'edit' && this.$route.query.id) {
        return {
          id: Number(this.$route.query.id),
          conditions,
          eventDisplayName: this.formData.eventName, // 使用事件名称作为显示名称
          eventName: this.formData.eventName,
          notes: this.formData.remark || '',
          productIds: this.formData.products || [], // 产品ID列表
          status: 1, // 默认启用状态
        }
      } else {
        return {
          conditions,
          eventDisplayName: this.formData.eventName, // 使用事件名称作为显示名称
          eventName: this.formData.eventName,
          notes: this.formData.remark || '',
          productIds: this.formData.products || [], // 产品ID列表
          status: 1, // 默认启用状态
        }
      }
    },

    // 将计算器文本中的 {事件名称} 转换为 {事件参数}
    convertEventNamesToParams(text) {
      if (!text) return text

      // 匹配所有 {xxx} 格式的内容
      return text.replace(/\{([^}]+)\}/g, (match, eventLabel) => {
        // 在 behaviorTypes 中查找对应的事件
        const behavior = this.behaviorTypes.find(item => item.label === eventLabel)

        if (behavior) {
          // 找到对应的事件，返回事件参数
          return `{${behavior.value}}`
        }

        // 没找到对应事件，保持原样
        return match
      })
    },

    // 将计算器文本中的 {事件参数} 转换为 {事件名称}
    convertEventParamsToNames(text) {
      if (!text) return text

      // 匹配所有 {xxx} 格式的内容
      return text.replace(/\{([^}]+)\}/g, (match, eventValue) => {
        // 在 behaviorTypes 中查找对应的事件
        const behavior = this.behaviorTypes.find(item => item.value === eventValue)

        if (behavior) {
          // 找到对应的事件，返回事件名称
          return `{${behavior.label}}`
        }

        // 没找到对应事件，保持原样
        return match
      })
    },

    // 将计算器纯文本转换为显示格式（识别事件参数并添加花括号）
    // 同时生成 tokens 数组用于新的展示方式
    // 例如: "level_complete2457*6789" -> "{关卡通过}2457*6789"
    convertCalculatorTextToDisplay(text) {
      if (!text) return ''

      let result = text

      // 获取所有事件类型，按值长度倒序排列（避免短的先匹配导致问题）
      const sortedBehaviorTypes = [...this.behaviorTypes].sort((a, b) => b.value.length - a.value.length)

      // 遍历所有事件类型，将匹配到的事件参数替换为 {事件名称}
      sortedBehaviorTypes.forEach(behavior => {
        // 创建一个正则表达式来匹配事件参数（确保不在花括号内）
        const regex = new RegExp(behavior.value, 'g')
        result = result.replace(regex, `{${behavior.label}}`)
      })

      return result
    },

    // 将计算器纯文本转换为 tokens 数组（处理不带花括号的纯文本格式）
    convertCalculatorTextToTokens(text) {
      if (!text) return []

      const tokens = []
      let currentText = ''

      // 首先识别所有的数据指标（事件参数）和运算符
      const sortedBehaviorTypes = [...this.behaviorTypes].sort((a, b) => b.value.length - a.value.length)
      const operators = ['+', '-', '*', '/', '(', ')']

      let i = 0
      while (i < text.length) {
        let matched = false

        // 尝试匹配事件参数
        for (const behavior of sortedBehaviorTypes) {
          if (text.substr(i, behavior.value.length) === behavior.value) {
            // 先保存之前的文本
            if (currentText) {
              tokens.push({
                type: 'text',
                value: currentText,
              })
              currentText = ''
            }

            // 添加数据指标 token
            tokens.push({
              type: 'metric',
              value: behavior.value,
              label: behavior.label,
            })
            i += behavior.value.length
            matched = true
            break
          }
        }

        if (matched) continue

        // 尝试匹配运算符
        if (operators.includes(text[i])) {
          // 先保存之前的文本
          if (currentText) {
            tokens.push({
              type: 'text',
              value: currentText,
            })
            currentText = ''
          }

          // 添加运算符 token
          const operatorDisplay = {
            '*': '×',
            '/': '÷',
          }
          tokens.push({
            type: 'operator',
            value: text[i],
            label: operatorDisplay[text[i]] || text[i],
          })
          i++
          continue
        }

        // 普通字符
        currentText += text[i]
        i++
      }

      // 添加最后剩余的文本
      if (currentText) {
        tokens.push({
          type: 'text',
          value: currentText,
        })
      }

      return tokens
    },

    // 将显示格式的计算器文本（带花括号）转换为 tokens 数组
    // 例如: "{关卡复活}{关卡开始}+{关卡开始}" -> tokens数组
    parseDisplayTextToTokens(text) {
      if (!text) return []

      const tokens = []
      const operators = ['+', '-', '*', '/', '(', ')']
      const operatorDisplay = {
        '*': '×',
        '/': '÷',
      }

      let i = 0
      let currentText = ''

      while (i < text.length) {
        // 检查是否是花括号包裹的数据指标
        if (text[i] === '{') {
          // 先保存之前累积的普通文本
          if (currentText) {
            tokens.push({
              type: 'text',
              value: currentText,
            })
            currentText = ''
          }

          // 查找配对的右花括号
          const endIndex = text.indexOf('}', i)
          if (endIndex !== -1) {
            const label = text.substring(i + 1, endIndex)

            // 根据 label 查找对应的 behavior
            const behavior = this.behaviorTypes.find(item => item.label === label)

            if (behavior) {
              tokens.push({
                type: 'metric',
                value: behavior.value,
                label: behavior.label,
              })
            } else {
              // 如果找不到对应的 behavior，作为普通文本处理
              tokens.push({
                type: 'text',
                value: `{${label}}`,
              })
            }

            i = endIndex + 1
            continue
          }
        }

        // 检查是否是运算符
        if (operators.includes(text[i])) {
          // 先保存之前累积的普通文本
          if (currentText) {
            tokens.push({
              type: 'text',
              value: currentText,
            })
            currentText = ''
          }

          tokens.push({
            type: 'operator',
            value: text[i],
            label: operatorDisplay[text[i]] || text[i],
          })
          i++
          continue
        }

        // 普通字符
        currentText += text[i]
        i++
      }

      // 保存最后剩余的文本
      if (currentText) {
        tokens.push({
          type: 'text',
          value: currentText,
        })
      }

      return tokens
    },

    // 将显示格式转换为纯文本格式（去掉花括号，保留事件参数）
    // 例如: "{关卡通过}2457*6789" -> "level_complete2457*6789"
    convertDisplayToCalculatorText(text) {
      if (!text) return ''

      // 匹配所有 {xxx} 格式的内容，将其转换为对应的事件参数
      return text.replace(/\{([^}]+)\}/g, (match, eventLabel) => {
        // 在 behaviorTypes 中查找对应的事件
        const behavior = this.behaviorTypes.find(item => item.label === eventLabel)

        if (behavior) {
          // 找到对应的事件，返回事件参数（不带花括号）
          return behavior.value
        }

        // 没找到对应事件，去掉花括号返回原文本
        return eventLabel
      })
    },

    // 构建阈值对象
    buildThresholdValue(item) {
      if (item.operatorValue === 'range') {
        // 区间类型：{"min": 1, "max": 10}
        return {
          min: item.value ? Number(item.value) : 0,
          max: item.value2 ? Number(item.value2) : 0,
        }
      } else {
        // 单值类型：{"value": 5}
        return {
          value: item.value ? Number(item.value) : 0,
        }
      }
    },

    async handleSave() {
      // 保存配置
      this.$refs.formRef.validate(async valid => {
        if (valid) {
          // 验证用户属性和行为条件必填
          if (this.userAttributes.length === 0) {
            this.$message.warning('请至少添加一条用户属性')
            return
          }

          if (this.behaviorConditions.length === 0) {
            this.$message.warning('请至少添加一条行为条件')
            return
          }

          // 验证用户属性和行为条件
          if (this.userAttributes.length > 0) {
            for (let i = 0; i < this.userAttributes.length; i++) {
              const item = this.userAttributes[i]
              if (!item.typeValue) {
                this.$message.warning(`请完善第${i + 1}条用户属性的配置`)
                return
              }
              const config = this.getUserAttrConfig(item.typeValue)
              if (config.hasOperator && !item.operatorValue) {
                this.$message.warning(`请完善第${i + 1}条用户属性的比较关系`)
                return
              }
              if (config.hasValue && !item.value && item.value !== 0) {
                this.$message.warning(`请完善第${i + 1}条用户属性的值`)
                return
              }
            }
          }

          if (this.behaviorConditions.length > 0) {
            for (let i = 0; i < this.behaviorConditions.length; i++) {
              const item = this.behaviorConditions[i]
              //   if (!item.timeDimensionValue) {
              //     this.$message.warning(`请完善第${i + 1}条行为条件的时间维度`)
              //     return
              //   }
              if (!item.showCalculator && !item.behaviorTypeValue) {
                this.$message.warning(`请完善第${i + 1}条行为条件的行为类型`)
                return
              }
              if (item.showCalculator && !item.showCalculatorText) {
                this.$message.warning(`请完善第${i + 1}条行为条件的自定义公式`)
                return
              }
              // 累计收入类型不需要比较关系
              const isRevenueType = ['ad_usd_revenue', 'pay_purchase_usd_revenue'].includes(item.behaviorTypeValue)
              if (!isRevenueType && !item.operatorValue) {
                this.$message.warning(`请完善第${i + 1}条行为条件的比较关系`)
                return
              }
              if (!item.value && item.value !== 0) {
                this.$message.warning(`请完善第${i + 1}条行为条件的阈值`)
                return
              }
              if (item.operatorValue === 'range' && !item.value2) {
                this.$message.warning(`请完善第${i + 1}条行为条件的区间最大值`)
                return
              }

              // 校验子集条件
              if (item.subConditions && item.subConditions.length > 0) {
                for (let j = 0; j < item.subConditions.length; j++) {
                  const subItem = item.subConditions[j]

                  if (!subItem.behaviorTypeValue) {
                    this.$message.warning(`请完善第${i + 1}条行为条件的第${j + 1}条子集条件的行为类型`)
                    return
                  }

                  // 累计收入类型不需要比较关系
                  const isSubRevenueType = ['ad_usd_revenue', 'pay_purchase_usd_revenue'].includes(
                    subItem.behaviorTypeValue,
                  )
                  if (!isSubRevenueType && !subItem.operatorValue) {
                    this.$message.warning(`请完善第${i + 1}条行为条件的第${j + 1}条子集条件的比较关系`)
                    return
                  }

                  if (!subItem.value && subItem.value !== 0) {
                    this.$message.warning(`请完善第${i + 1}条行为条件的第${j + 1}条子集条件的阈值`)
                    return
                  }

                  if (subItem.operatorValue === 'range' && !subItem.value2) {
                    this.$message.warning(`请完善第${i + 1}条行为条件的第${j + 1}条子集条件的区间最大值`)
                    return
                  }
                }
              }
            }
          }

          try {
            this.saveLoading = true

            // 使用 formParams 方法构建保存数据
            const saveData = this.formParams()

            // 调试用
            // if (Object.keys(saveData)) return console.log('[ saveData ] >', saveData)

            const res = await saveBuyEvent(saveData)

            if (res.code === '0000') {
              this.$message.success('保存成功')
              // 返回列表页
              setTimeout(() => {
                this.handleBack()
              }, 500)
            }
          } catch (error) {
            console.error('保存配置失败:', error)
            this.$message.error('保存失败')
          } finally {
            this.saveLoading = false
          }
        } else {
          this.$message.error('请完善必填信息')
          return false
        }
      })
    },
  },
}
</script>

<style lang="less" scoped>
.event-config-wrap {
  margin: 16px;
  height: calc(100vh - 136px);
  overflow: auto;
  padding: 24px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 1);
  box-shadow: 0px 2px 12px rgba(0, 0, 0, 0.12);

  .event-title {
    font-size: 16px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.85);
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid #f0f0f0;

    .el-icon-arrow-left {
      font-size: 16px;
      cursor: pointer;
      margin-right: 8px;
      &:hover {
        color: #40a9ff;
      }
    }
  }

  .section-container {
    margin-bottom: 32px;

    .section-title {
      font-size: 15px;
      font-weight: 600;
      color: rgba(0, 0, 0, 0.85);
      margin-bottom: 20px;
    }

    .event-form {
      ::v-deep .el-form-item {
        margin-bottom: 24px;
      }

      ::v-deep .el-form-item__label {
        color: rgba(0, 0, 0, 0.85);
        font-weight: 400;
      }
    }
  }

  .condition-block {
    margin-bottom: 32px;

    .condition-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 16px;

      .condition-label {
        font-size: 14px;
        font-weight: 500;
        color: rgba(0, 0, 0, 0.85);
      }

      .logic-toggle-btn {
        color: #1890ff;
        font-size: 12px;
        padding: 4px 8px;

        &:hover {
          color: #40a9ff;
        }
      }
    }

    .condition-content {
      position: relative;
      padding-left: 54px;

      &::before {
        content: '';
        position: absolute;
        left: 8px;
        top: 4px;
        bottom: 32px;
        width: 3px;
        background: #1890ff;
        border-radius: 2px;
      }

      .condition-list-wrapper {
        position: relative;
        margin-bottom: 12px;
      }

      .condition-badge-center {
        position: absolute;
        left: -36px;
        top: 50%;
        transform: translateY(-50%);
        width: 28px;
        height: 28px;
        line-height: 28px;
        text-align: center;
        background: #fafafa;
        border: 1px solid #d9d9d9;
        border-radius: 2px;
        font-size: 12px;
        color: rgba(0, 0, 0, 0.65);
        z-index: 1;

        &.clickable {
          cursor: pointer;
          user-select: none;

          &:hover {
            background: #e6f7ff;
            border-color: #1890ff;
            color: #1890ff;
          }
        }
      }

      .condition-list {
        margin-bottom: 0;
      }

      .condition-item {
        margin-bottom: 12px;

        &:last-child {
          margin-bottom: 0;
        }

        .condition-row {
          display: flex;
          align-items: center;
          min-height: 32px;

          .condition-selects {
            // min-width: 750px;
            display: flex;
            align-items: center;
            gap: 8px;
            flex-wrap: wrap;

            .opera-icon {
              cursor: pointer;
              width: 16px;
              height: 16px;
              font-size: 16px;

              &.active-icon {
                color: #1890ff;
              }

              &:hover {
                color: #40a9ff;
              }
            }
          }

          .condition-tags {
            flex: 1;
            display: flex;
            align-items: center;
            gap: 8px;
            flex-wrap: wrap;
          }

          .condition-tag {
            height: 26px;
            line-height: 24px;
            padding: 0 12px;
            background: #fafafa;
            border: 1px solid #d9d9d9;
            border-radius: 2px;
            font-size: 13px;
            color: rgba(0, 0, 0, 0.85);
          }

          .range-separator {
            margin: 0 4px;
            color: rgba(0, 0, 0, 0.65);
          }

          .condition-select {
            min-width: 120px;

            ::v-deep .el-input__inner {
              height: 28px;
              line-height: 28px;
              border-radius: 2px;
              font-size: 13px;
            }

            ::v-deep .el-input__suffix {
              display: flex;
              align-items: center;
            }
          }

          .custom-text {
            box-sizing: border-box;
            width: 192px;
            height: 28px;
            line-height: 28px;
            font-size: 13px;
            border: 1px solid #dcdfe6;
            border-radius: 2px;
            padding: 0 30px 0 15px;
            color: #777777;
          }

          .condition-input {
            width: 120px;

            ::v-deep .el-input__inner {
              height: 28px;
              line-height: 28px;
              padding: 0 8px;
              border-radius: 2px;
              font-size: 13px;
            }
          }

          .retention-input-wrapper {
            display: flex;
            align-items: center;
            gap: 8px;

            .retention-input-number {
              width: 120px;
              line-height: 1;

              /deep/ .el-input-number__increase,
              /deep/ .el-input-number__decrease {
                line-height: 10px;
              }

              /deep/ .el-input__inner {
                height: 28px;
                line-height: 28px;
                border-radius: 2px;
                font-size: 13px;
              }
            }

            .retention-text {
              font-size: 13px;
              color: rgba(0, 0, 0, 0.85);
            }
          }

          .special-text {
            font-size: 13px;
            color: rgba(0, 0, 0, 0.85);
          }

          .revenue-input-number {
            width: 150px;
            line-height: 1;

            /deep/ .el-input-number__increase,
            /deep/ .el-input-number__decrease {
              line-height: 10px;
            }

            /deep/ .el-input__inner {
              height: 28px;
              line-height: 28px;
              border-radius: 2px;
              font-size: 13px;
            }
          }

          .delete-btn {
            color: #ff4d4f;
            font-size: 18px;
            padding: 0;
            margin-left: 16px;
            flex-shrink: 0;

            &:hover {
              color: #ff7875;
            }

            i {
              font-size: 18px;
            }
          }
        }

        // 计算器面板
        .calculator-panel {
          margin-top: 12px;
          padding: 16px;
          background: #ffffff;
          border: 1px solid #d9d9d9;
          border-radius: 4px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

          .calculator-header {
            font-size: 14px;
            font-weight: 500;
            color: rgba(0, 0, 0, 0.85);
            margin-bottom: 12px;
          }

          .calculator-buttons {
            margin-bottom: 12px;

            .button-row {
              display: flex;
              gap: 6px;
              flex-wrap: wrap;
              align-items: center;

              &.operator-row {
                .calc-btn {
                  min-width: 32px;
                }
              }
            }

            .calc-btn {
              height: 28px;
              min-width: 32px;
              padding: 0 10px;
              background: #ffffff;
              border: 1px solid #d9d9d9;
              border-radius: 2px;
              font-size: 13px;
              color: rgba(0, 0, 0, 0.85);
              cursor: pointer;
              transition: all 0.3s;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              white-space: nowrap;

              &:hover {
                color: #1890ff;
                border-color: #1890ff;
              }

              &:active {
                background: #f0f0f0;
              }

              &.operator-btn {
                font-weight: 500;
                font-size: 14px;
              }

              &.insert-event-btn {
                background: #ffffff;
                color: rgba(0, 0, 0, 0.65);
                padding: 0 10px;

                i {
                  margin-right: 4px;
                  font-size: 12px;
                }

                &:hover {
                  color: #1890ff;
                  border-color: #1890ff;
                }
              }

              i {
                font-size: 14px;
              }
            }
          }

          .calculator-display {
            width: 100%;

            ::v-deep .el-textarea__inner {
              min-height: 80px !important;
              font-size: 14px;
              line-height: 1.5;
              border-radius: 2px;
            }
          }

          .calculator-display-container {
            width: 98%;
            min-height: 80px;
            padding: 8px 12px;
            border: 1px solid #dcdfe6;
            border-radius: 2px;
            background: #ffffff;
            cursor: text;
            transition: border-color 0.3s;

            &:hover {
              border-color: #c0c4cc;
            }

            &:focus-within {
              border-color: #1890ff;
              outline: none;
            }

            .calculator-display-tags {
              display: flex;
              flex-wrap: wrap;
              align-items: flex-start;
              gap: 4px;
              min-height: 60px;

              .calculator-tag {
                margin: 0;
                height: 28px;
                line-height: 26px;
                font-size: 13px;
                background: #ecf5ff;
                border-color: #d9ecff;
                color: #409eff;
                cursor: default;
                user-select: none;

                ::v-deep .el-tag__close {
                  color: #409eff;

                  &:hover {
                    background-color: #409eff;
                    color: #ffffff;
                  }
                }
              }

              .calculator-text {
                font-size: 14px;
                line-height: 28px;
                color: rgba(0, 0, 0, 0.85);
                white-space: pre-wrap;
                word-break: break-all;
              }

              .calculator-input {
                border: none;
                outline: none;
                font-size: 14px;
                line-height: 28px;
                color: rgba(0, 0, 0, 0.85);
                background: transparent;

                &::placeholder {
                  color: #c0c4cc;
                }
              }
            }
          }
        }

        // 子集面板
        .subproject-panel {
          margin-top: 12px;
          margin-left: 16px;
          padding: 16px;
          background: #fafafa;
          border: 1px solid #d9d9d9;
          border-radius: 4px;

          .condition-content {
            position: relative;
            padding-left: 54px;

            &::before {
              content: '';
              position: absolute;
              left: 8px;
              top: 4px;
              bottom: 32px;
              width: 3px;
              background: #1890ff;
              border-radius: 2px;
            }

            .condition-badge-center {
              position: absolute;
              left: -36px;
              top: 50%;
              transform: translateY(-50%);
              width: 28px;
              height: 28px;
              line-height: 28px;
              text-align: center;
              background: #ffffff;
              border: 1px solid #d9d9d9;
              border-radius: 2px;
              font-size: 12px;
              color: rgba(0, 0, 0, 0.65);
              z-index: 1;

              &.clickable {
                cursor: pointer;
                user-select: none;

                &:hover {
                  background-color: #e6f7ff;
                  border-color: #1890ff;
                  color: #1890ff;
                }
              }
            }

            .condition-list-wrapper {
              position: relative;
              margin-bottom: 12px;
            }

            .condition-list {
              margin-bottom: 0;
            }

            .condition-item {
              margin-bottom: 12px;

              &:last-child {
                margin-bottom: 0;
              }

              .condition-row {
                display: flex;
                align-items: center;
                min-height: 32px;

                .condition-selects {
                  min-width: auto;
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  flex-wrap: wrap;
                }

                .delete-btn {
                  color: #ff4d4f;
                  font-size: 18px;
                  padding: 0;
                  margin-left: 16px;
                  flex-shrink: 0;

                  &:hover {
                    color: #ff7875;
                  }

                  i {
                    font-size: 18px;
                  }
                }
              }
            }

            .add-btn {
              position: relative;
              left: -50px;
              color: #1890ff;
              font-size: 13px;
              padding: 4px 0;
              display: flex;
              align-items: center;

              &:hover {
                color: #40a9ff;
              }

              i {
                font-size: 14px;
                margin-right: 4px;
              }
            }
          }
        }
      }

      .add-btn {
        position: relative;
        left: -50px;
        color: #1890ff;
        font-size: 13px;
        padding: 4px 0;
        display: flex;
        align-items: center;

        &:hover {
          color: #40a9ff;
        }

        i {
          font-size: 14px;
          margin-right: 4px;
        }
      }
    }
  }

  .footer-btns {
    display: flex;
    justify-content: space-between;
    margin-top: 40px;
    padding-top: 24px;
    border-top: 1px solid #f0f0f0;

    .footer-btns-right {
      display: flex;
    }

    .el-button {
      min-width: 100px;
      margin: 0 8px;
    }
  }
}

// Element UI 组件样式覆盖
::v-deep .el-button--primary {
  background-color: #1890ff;
  border-color: #1890ff;

  &:hover {
    background-color: #40a9ff;
    border-color: #40a9ff;
  }
}

::v-deep .el-select .el-tag {
  max-width: 70% !important;
}

::v-deep .el-tag {
  border: none;
}

::v-deep .el-select {
  .el-input__inner {
    border-radius: 2px;
  }
}

::v-deep .el-input__inner {
  border-radius: 2px;

  &:focus {
    border-color: #1890ff;
  }
}

::v-deep .el-textarea__inner {
  border-radius: 2px;

  &:focus {
    border-color: #1890ff;
  }
}
</style>

<style lang="less">
// 事件选择器 popover 全局样式
.event-selector-popover {
  padding: 0 !important;
  max-height: 300px;
  overflow: hidden;

  .event-selector-list {
    max-height: 300px;
    overflow-y: auto;

    .event-selector-item {
      padding: 8px 16px;
      font-size: 13px;
      color: rgba(0, 0, 0, 0.85);
      cursor: pointer;
      transition: all 0.3s;

      &:hover {
        background-color: #f5f5f5;
        color: #1890ff;
      }

      &:active {
        background-color: #e6f7ff;
      }
    }
  }
}
</style>
